import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Funnel,
  CrmFunnelsService,
  FunnelCreateBodyParams,
  FunnelUpdateBodyParams,
} from 'src/api/generated';

interface UseCreateFunnelMutationResponse {
  createFunnel: (data: FunnelCreateBodyParams) => Promise<Funnel>;
  loading: boolean;
  error: Error | null;
}

export const useCreateFunnel = (
  onSuccess: (funnel: Funnel) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseCreateFunnelMutationResponse => {
  const queryClient = useQueryClient();

  const createFunnel = async (
    data: FunnelCreateBodyParams
  ): Promise<Funnel> => {
    return await CrmFunnelsService.createFunnel(data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: createFunnel,
    onSuccess: (funnel) => {
      queryClient.invalidateQueries({ queryKey: ['crm/funnels'] });
      onSuccess(funnel);
    },
    onError: (error) => {
      onError(error);
    },
  });

  return {
    createFunnel: mutateAsync,
    loading,
    error,
  };
};

interface UseUpdateFunnelMutationResponse {
  updateFunnel: (
    params: { id: number } & FunnelUpdateBodyParams
  ) => Promise<Funnel>;
  loading: boolean;
  error: Error | null;
}

export const useUpdateFunnel = (
  onSuccess: (funnel: Funnel) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseUpdateFunnelMutationResponse => {
  const queryClient = useQueryClient();

  const updateFunnel = async ({
    id,
    ...data
  }: { id: number } & FunnelUpdateBodyParams): Promise<Funnel> => {
    return await CrmFunnelsService.updateFunnel(id, data);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: updateFunnel,
    onSuccess: (funnel) => {
      queryClient.invalidateQueries({ queryKey: ['crm/funnels'] });
      queryClient.invalidateQueries({ queryKey: ['crm/funnels/statuses'] });
      onSuccess(funnel);
    },
    onError: (error) => {
      onError(error);
    },
  });

  return {
    updateFunnel: mutateAsync,
    loading,
    error,
  };
};

interface UseDeleteFunnelMutationResponse {
  deleteFunnel: (id: number) => Promise<Funnel>;
  loading: boolean;
  error: Error | null;
}

export const useDeleteFunnel = (
  onSuccess: (funnel: Funnel) => void = () => {},
  onError: (error: Error) => void = () => {}
): UseDeleteFunnelMutationResponse => {
  const queryClient = useQueryClient();

  const deleteFunnel = async (id: number): Promise<Funnel> => {
    return await CrmFunnelsService.deleteFunnel(id);
  };

  const {
    mutateAsync,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: deleteFunnel,
    onSuccess: (funnel) => {
      queryClient.invalidateQueries({ queryKey: ['crm/funnels'] });
      onSuccess(funnel);
    },
    onError: (error) => {
      onError(error);
    },
  });

  return {
    deleteFunnel: mutateAsync,
    loading,
    error,
  };
};
